/**
 * ============================================================================
 *  helperLines — 节点对齐检测（纯函数）
 * ============================================================================
 *
 * 在 onNodesChange 拦截被拖节点的位置变更，与画布上其它节点逐一比对边界，
 * 找到「快要对齐」的位置：
 *   - 垂直对齐线（vertical）：左对左 / 右对右 / 左对右 / 右对左 / 水平中心对齐
 *   - 水平对齐线（horizontal）：上对上 / 下对下 / 上对下 / 下对上 / 垂直中心对齐
 *
 * 命中（差值 ≤ distance，画布坐标）时：
 *   - 返回对齐线坐标（horizontal / vertical，画布坐标）用于绘制蓝色虚线
 *   - 返回吸附后的 snapPosition，修正被拖节点的落点
 *
 * distance 取画布坐标固定值（不随 zoom 缩放），保证缩放后吸附手感一致。
 */

// 取节点尺寸：优先 ReactFlow 测量值，回退到显式锁定尺寸，最后默认
const nodeW = (n) => n.width ?? n.data?.customSize?.width ?? 320;
const nodeH = (n) => n.height ?? n.data?.customSize?.height ?? 240;

/**
 * @param {object} change   - onNodesChange 里 type==='position' 的变更对象
 * @param {Array}  nodes    - 当前所有节点
 * @param {number} distance - 吸附触发距离（画布坐标），默认 5px
 * @returns {{ horizontal?: number, vertical?: number, snapPosition: {x?: number, y?: number} }}
 */
export function getHelperLines(change, nodes, distance = 5) {
  const defaultResult = {
    horizontal: undefined,
    vertical: undefined,
    snapPosition: { x: undefined, y: undefined },
  };

  const nodeA = nodes.find((n) => n.id === change.id);
  if (!nodeA || !change.position) return defaultResult;

  const aW = nodeW(nodeA);
  const aH = nodeH(nodeA);
  const aBounds = {
    left: change.position.x,
    right: change.position.x + aW,
    top: change.position.y,
    bottom: change.position.y + aH,
    centerX: change.position.x + aW / 2,
    centerY: change.position.y + aH / 2,
  };

  let hDist = distance; // 当前最优水平对齐线的距离
  let vDist = distance; // 当前最优垂直对齐线的距离

  return nodes
    .filter((n) => n.id !== nodeA.id && n.type !== 'group' && !n.hidden)
    .reduce((result, nodeB) => {
      const bW = nodeW(nodeB);
      const bH = nodeH(nodeB);
      const b = {
        left: nodeB.position.x,
        right: nodeB.position.x + bW,
        top: nodeB.position.y,
        bottom: nodeB.position.y + bH,
        centerX: nodeB.position.x + bW / 2,
        centerY: nodeB.position.y + bH / 2,
      };

      // ── 垂直对齐线（影响 x） ──
      // 左对左
      const ll = Math.abs(aBounds.left - b.left);
      if (ll < vDist) { result.snapPosition.x = b.left; result.vertical = b.left; vDist = ll; }
      // 右对右
      const rr = Math.abs(aBounds.right - b.right);
      if (rr < vDist) { result.snapPosition.x = b.right - aW; result.vertical = b.right; vDist = rr; }
      // 左对右
      const lr = Math.abs(aBounds.left - b.right);
      if (lr < vDist) { result.snapPosition.x = b.right; result.vertical = b.right; vDist = lr; }
      // 右对左
      const rl = Math.abs(aBounds.right - b.left);
      if (rl < vDist) { result.snapPosition.x = b.left - aW; result.vertical = b.left; vDist = rl; }
      // 水平中心对齐
      const cx = Math.abs(aBounds.centerX - b.centerX);
      if (cx < vDist) { result.snapPosition.x = b.centerX - aW / 2; result.vertical = b.centerX; vDist = cx; }

      // ── 水平对齐线（影响 y） ──
      // 上对上
      const tt = Math.abs(aBounds.top - b.top);
      if (tt < hDist) { result.snapPosition.y = b.top; result.horizontal = b.top; hDist = tt; }
      // 下对下
      const bb = Math.abs(aBounds.bottom - b.bottom);
      if (bb < hDist) { result.snapPosition.y = b.bottom - aH; result.horizontal = b.bottom; hDist = bb; }
      // 上对下
      const tb = Math.abs(aBounds.top - b.bottom);
      if (tb < hDist) { result.snapPosition.y = b.bottom; result.horizontal = b.bottom; hDist = tb; }
      // 下对上
      const bt = Math.abs(aBounds.bottom - b.top);
      if (bt < hDist) { result.snapPosition.y = b.top - aH; result.horizontal = b.top; hDist = bt; }
      // 垂直中心对齐
      const cy = Math.abs(aBounds.centerY - b.centerY);
      if (cy < hDist) { result.snapPosition.y = b.centerY - aH / 2; result.horizontal = b.centerY; hDist = cy; }

      return result;
    }, defaultResult);
}
